import {ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useId, useState} from "react";

export function jsonPost(url: string, payload: any) {
    return fetch(url, {method: 'POST', headers: {"Content-Type": "application/json"}, body: JSON.stringify(payload)})
}

export function jsonPut(url: string, payload: any) {
    return fetch(url, {method: 'PUT', headers: {"Content-Type": "application/json"}, body: JSON.stringify(payload)})
}

// stolen from https://github.com/microsoft/TypeScript/issues/37663#issuecomment-606110995
export type Initializer<T> = T extends Function ? never : (T | (() => T))

export function useLocalStorageState<T>(key: string, fallbackState?: Initializer<T>) {
    return usePersistedState<T>(window.localStorage, key, fallbackState)
}

function usePersistedState<T>(store: Storage, key: string, fallbackState?: Initializer<T>): [T, Dispatch<SetStateAction<T>>] {
    const [state, updateState] = useState<T>(() => {
        const savedItem = store.getItem(key)
        if (savedItem) {
            return JSON.parse(savedItem)
        } else {
            return (typeof fallbackState == 'function' ? fallbackState() : fallbackState)
        }
    })

    // The key really ought not change, but it will be less bad if we start writing to a new location than
    // if we keep writing to the old one
    const customSetValue = useCallback((newValue: T | ((prev: T) => T)) => {
        updateState((valueWas) => {
            const computedValue = newValue instanceof Function ? newValue(valueWas) : newValue;
            store.setItem(key, JSON.stringify(computedValue));
            return computedValue;
        })
    }, [key]);

    return [state, customSetValue]
}

export function useSessionStorageState<T>(key: string, fallbackState?: Initializer<T>) {
    return usePersistedState<T>(window.sessionStorage, key, fallbackState)
}

/* quick little helper to combine id & state variables */
export function useCheckboxState(init: boolean = false) {
    const [value, setValue] = useState(init);
    const id = useId();
    return {checked: value, set: setValue, onChange: (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.checked), id: id};
}

export interface DataTableExportSelector<T> {
    name: string,
    export?: boolean,
    exportKey?: keyof T,
    exportFcn?: (row : T) => string,
    selector?: (row: T, rowIndex?: number) => string | number | boolean
}
// From https://jbetancur.github.io/react-data-table-component/?path=/story/examples-export-csv--export-csv
// Blatant "inspiration" from https://codepen.io/Jacqueline34/pen/pyVoWr
export function dataTableExport<T>(array: T[], selectors: DataTableExportSelector<T>[], format : "csv" | "tsv" = "csv", filename?: string) {
    const link = document.createElement('a');
    let result : string;

    const columnDelimiter = format === "csv" ? ',' : '\t';
    const lineDelimiter = '\n';

    result = '';
    result += selectors.filter(x => x.exportKey || x.exportFcn || x.export).map(x => x.name).join(columnDelimiter);
    result += lineDelimiter;

    array.forEach(item => {
        let ctr = 0;
        selectors.forEach(selector => {
            if (ctr > 0) result += columnDelimiter;
            let toAppend;
            if (selector.export) {
                toAppend = selector.selector ? selector.selector(item) : "";
            } else if (selector?.exportKey) {
                toAppend = item[selector.exportKey];
            } else if (selector?.exportFcn) {
                toAppend = selector.exportFcn(item);
            } else {
                // dont include this column
                return;
            }
            if (toAppend != null && typeof toAppend === "string") {
                if (toAppend.includes(",") || toAppend.includes('"') || toAppend.includes("\n")) {
                    toAppend = `"${toAppend.replace(/"/g, '""')}"`;
                }
            }
            result += toAppend;
            ctr++;
        });
        result += lineDelimiter;
    });

    if (result == null) return;

    filename = filename ? filename : `export.${format}`;

    const bom = "%EF%BB%BF"; // force excel to open UTF-8 properly
    if (!result.match(format === "csv" ? /^data:text\/csv/i : /^data:text\/tsv/i)) {
        result = `data:text/${format};charset=utf-8,${bom}${encodeURIComponent(result)}`;
    }

    link.setAttribute('href', result);
    link.setAttribute('download', filename);
    link.click();
}

export const useDebouncedEffect = (effect: () => void, deps: any[], delay: number) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
    }, [...(deps || []), delay]);
}